//import { color } from "html2canvas/dist/types/css/types/color";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import "../styles/scrollbar.css";
import { useTranslation } from 'react-i18next';
import consultationImg from "./Assets/consultation.png";
import gemImg from "./Assets/gem.png";
import fallbackImg from "./Assets/XianheLogo.png";

const styles = {
  container: {
    height: "90vh",
    overflowY: "auto",
    backgroundColor: "black",
    padding: "16px",
  },
  wrapper: {
    maxWidth: "1000px",
    margin: "0 auto",
  },
  heading: {
    fontSize: "28px",
    fontWeight: "bold",
    marginBottom: "24px",
    color: "#DFBD69",
  },
  card: {
    display: "flex",
    gap: "1%",
    padding: "16px",
    //backgroundColor: "#DFBD69",
    borderRadius: "12px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
    alignItems: "center",
    color: "black",
    marginBottom: "1%",
  },
  image: {
    width: "96px",
    height: "96px",
    objectFit: "cover",
    borderRadius: "8px",
    backgroundColor: "#e5e7eb",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "12px", color: "#6b7280",
  },
  noimage: {
    width: "96px",
    height: "96px",
    backgroundColor: "transparent",
    borderRadius: "8px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "12px",
    color: "transparent",
  },
  details: {
    flex: 1,
    color: "#DFBD69",
  },
  toolbar: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    alignItems: "center",
    marginBottom: 12,
  },
  tab: {
    padding: "6px 14px",
    borderRadius: 999,
    border: "1px solid rgba(223,189,105,0.4)",
    background: "transparent",
    color: "#DFBD69",
    cursor: "pointer",
    fontSize: 14,
    whiteSpace: "nowrap",
  },
  tabActive: {
    background: "#DFBD69",
    color: "#111",
    borderColor: "#DFBD69",
    fontWeight: 700,
  },
  search: {
    flex: "1 1 220px",
    minWidth: 0,
    padding: "8px 12px",
    borderRadius: 8,
    border: "1px solid rgba(223,189,105,0.4)",
    background: "rgba(255,255,255,0.04)",
    color: "#F3CC5C",
    fontSize: 14,
    outline: "none",
  },
  resultsCount: {
    color: "rgba(243,204,92,0.7)",
    fontSize: 13,
    margin: "0 0 16px",
  },
  pagination: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    justifyContent: "center",
    alignItems: "center",
    marginTop: 24,
    paddingBottom: 8,
  },
  pageBtn: {
    minWidth: 36,
    height: 36,
    padding: "0 10px",
    borderRadius: 8,
    border: "1px solid rgba(223,189,105,0.4)",
    background: "transparent",
    color: "#DFBD69",
    cursor: "pointer",
    fontSize: 14,
  },
  pageBtnActive: {
    background: "#DFBD69",
    color: "#111",
    borderColor: "#DFBD69",
    fontWeight: 700,
  },
  pageBtnDisabled: {
    opacity: 0.35,
    cursor: "default",
  },
};

const PAGE_SIZE = 8;

const FILTERS = [
  { key: "all", labelKey: "filterAll" },
  { key: "amulets", labelKey: "filterAmulets" },
  { key: "appointments", labelKey: "filterAppointments" },
  { key: "packs", labelKey: "filterPacks" },
  { key: "other", labelKey: "filterOther" },
];

// Bucket a purchase row into one of the filter tabs. DocNo prefixes:
// PB = booking/appointment, PCH = amulet/charm/physical product,
// PD = draws, PQ = question packs, everything else (PS, PH, PC, ...) = other.
const getItemCategory = (item) => {
  const prefix = (item?.Prefix || "").toUpperCase();
  const desc = (item?.ProdDesc || "").toLowerCase();
  if (prefix === "PB" || desc.includes("appointment")) return "appointments";
  if (prefix === "PD" || prefix === "PQ" || /\bdraw|\bquestion/.test(desc)) return "packs";
  if (prefix === "PCH") return "amulets";
  return "other";
};

// Compact page list: 1 … 4 5 6 … 20
const getPageNumbers = (total, current) => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const wanted = new Set([1, total, current, current - 1, current + 1]);
  const sorted = [...wanted].filter((p) => p >= 1 && p <= total).sort((a, b) => a - b);
  const out = [];
  let prev = 0;
  for (const p of sorted) {
    if (p - prev > 1) out.push("…");
    out.push(p);
    prev = p;
  }
  return out;
};

// Dummy data for styling purposes
const dummyHistory = [
  {
    PID: 1,
    QTY: -50,
    STATUS: 0,
    REC_DATE: "2024-10-01T10:30:00"
  },
  {
    PID: 2,
    QTY: -75,
    STATUS: 1,
    REC_DATE: "2024-09-28T14:15:00"
  },
  {
    PID: 3,
    QTY: -100,
    STATUS: 0,
    REC_DATE: "2024-09-25T09:20:00"
  }
];

const dummyProducts = [
  {
    ID: 1,
    FILENAME: "Magic Sword",
    CATEGORY_DESC: "Weapons",
    REMARK: "A powerful magical sword with fire enchantment"
  },
  {
    ID: 2,
    FILENAME: "Crystal Shield",
    CATEGORY_DESC: "Armor",
    REMARK: "Protective shield made from enchanted crystal"
  },
  {
    ID: 3,
    FILENAME: "Healing Potion",
    CATEGORY_DESC: "Consumables",
    REMARK: "Restores health instantly when consumed"
  }
];

// Image URLs from the purchase-history API can contain raw spaces / non-ASCII
// characters (e.g. ".../images/employee/0013/Guat Lian.png"). Some WebViews and
// image servers won't load those unless they're percent-encoded, so normalise
// here before handing the URL to <img>.
const encodeImageUrl = (url) => {
  if (!url || typeof url !== "string") return "";
  const trimmed = url.trim();
  if (!trimmed) return "";
  // Leave already-encoded URLs untouched to avoid double-encoding.
  if (/%[0-9A-Fa-f]{2}/.test(trimmed)) return trimmed;
  try {
    return encodeURI(trimmed);
  } catch {
    return trimmed;
  }
};

// Many rows (gem packs, draws, question packs, partner/affiliate entries) come
// back with no image at all. Show a sensible placeholder instead of a blank gap.
const getHistoryFallbackImage = (item) => {
  const prefix = (item?.Prefix || "").toUpperCase();
  if (prefix === "PB") return consultationImg; // appointments / consultations
  if (prefix === "PD" || prefix === "PQ") return gemImg; // draws / question packs
  return fallbackImg;
};

const getProductImageUrl = (product) => {
  // If your API returns an ImageUrl field, use it directly
  if (product?.ImageUrl) {
    return product.ImageUrl;
  }
  // Fallback to your previous logic if ImageUrl is not present
  if (product?.ID && product?.FILENAME) {
    return `${
      process.env.PUBLIC_URL
    }/static/media/product/${product.ID.toString().padStart(5, "0")}/${
      product.FILENAME
    }.png`;
  }
  return null;
};

// const GROUP_TYPE_LABELS = {
//   0: "Free",
//   1: "Daily",
//   2: "Weekly",
//   3: "Monthly",
//   4: "Yearly",
// };

const PurchaseHistory = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const userID = localStorage.getItem("userID");
  const [history, setHistory] = useState([]);
  const [productList, setProductList] = useState([]);
  const [groupedHistory, setGroupedHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [useDummyData, setUseDummyData] = useState(false);
  // Track rows whose thumbnail URL failed to load (e.g. a missing employee
  // photo on the server) so we can show the empty placeholder instead of a
  // broken-image icon.
  const [failedImages, setFailedImages] = useState({});

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const res = await fetch(
          `https://xianhefairy.com/LotusFairy_API/api/booking/GetPurchaseHistory?ID=${userID}`
        );
        const data = await res.json();
        // Fix: Ensure we get an array from the API response
        const historyArray = Array.isArray(data) ? data : (data?.Table || []);
        setHistory(historyArray);
        // console.log("[PurchaseHistory] GetPurchaseHistory (booking)", {
        //   status: res.status,
        //   count: historyArray.length,
        //   historyArray,
        // });
      } catch (err) {
        console.error("Failed to fetch purchase history", err);
        // Use dummy data on error
        setUseDummyData(true);
        setHistory(dummyHistory);
        setProductList(dummyProducts);
      }
    };

    const fetchProducts = async () => {
      try {
        const res = await fetch(
          "https://xianhefairy.com/LotusFairy_API/api/member/getProduct"
        );
        const data = await res.json();
        const productsArray = Array.isArray(data) ? data : [];
        setProductList(productsArray);
        // console.log("Product", productsArray);
      } catch (err) {
        console.error("Failed to fetch product list", err);
        // Use dummy data on error
        setUseDummyData(true);
        setHistory(dummyHistory);
        setProductList(dummyProducts);
      }
    };

    // For styling purposes, use dummy data immediately
    // if (process.env.NODE_ENV === 'development') {
    //   setHistory(dummyHistory);
    //   setProductList(dummyProducts);
    //   setLoading(false);
    //   setUseDummyData(true);
    //   return;
    // }

    // Run both in parallel and wait both finish before setting loading false
    Promise.all([fetchHistory(), fetchProducts()]).then(() =>
      setLoading(false)
    );
  }, [userID]);

  const getSortedItems = () => {
    // Flatten all items and keep group info
    const allItems = groupedHistory.flatMap(group =>
      group.Data.map(item => ({
        ...item,
        groupName: group.GroupName
      }))
    );
    // Sort by RecDate (convert to Date for accuracy)
    return allItems.sort((a, b) => new Date(b.RecDate) - new Date(a.RecDate));
  };

  const getItemDetails = (item) => {
    const product = productList.find((p) => p.ID === item.PID);
    return product
      ? {
          ...product,
          quantity: 1,
          transactionDate: item.REC_DATE,
          coins: item.QTY, // keep the sign as-is
          purchaseMethod:
            item.STATUS === 0 ? "Purchased by Coin" : "Purchased by Card",
        }
      : null;
  };

  // useEffect(() => {
  //   const fetchGroupedHistory = async () => {
  //     try {
  //       const res = await fetch(
  //         `https://xianhefairy.com/LotusFairy_API/api/Product/GetHistoryPurchase?CustId=${userID}`
  //       );
  //       const data = await res.json();
  //       setGroupedHistory(Array.isArray(data) ? data : []);
  //     } catch (err) {
  //       console.error("Failed to fetch grouped purchase history", err);
  //       setGroupedHistory([]);
  //     } finally {
  //       setLoading(false);
  //     }
  //   };
  //   fetchGroupedHistory();
  // }, [userID]);

  useEffect(() => {
    const fetchGroupedHistory = async () => {
      const url = `https://xianhefairy.com/LotusFairy_API/api/Product/GetPurchaseHistorySort?CustID=${userID}`;
      // console.log("[PurchaseHistory] GET", url, {
      //   userID,
      //   typeofUserID: typeof userID,
      // });
      try {
        const res = await fetch(url);
        // Read as text first so a non-JSON/error body stays visible instead of
        // being swallowed by res.json() throwing.
        const text = await res.text();
        // console.log("[PurchaseHistory] GetPurchaseHistorySort response", {
        //   status: res.status,
        //   ok: res.ok,
        //   url: res.url,
        //   body: text,
        // });

        let data;
        try {
          data = JSON.parse(text);
        } catch {
          console.warn("[PurchaseHistory] response body was not JSON");
          data = null;
        }

        // console.log("[PurchaseHistory] parsed", {
        //   isArray: Array.isArray(data),
        //   length: Array.isArray(data) ? data.length : undefined,
        //   data,
        // });
        if (Array.isArray(data) && data.length > 0) {
          // console.log("[PurchaseHistory] first row", data[0]);
        }
        if (!Array.isArray(data)) {
          console.warn(
            "[PurchaseHistory] API did not return an array - the list renders empty",
            data,
          );
        }

        setGroupedHistory(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Failed to fetch grouped purchase history", err);
        setGroupedHistory([]);
      } finally {
        setLoading(false);
      }
    };
    fetchGroupedHistory();
  }, [userID]);

  // --- Lazada-style filter tabs + search + pagination ---
  const [activeFilter, setActiveFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const listTopRef = useRef(null);

  const filteredItems = useMemo(() => {
    const rows = Array.isArray(groupedHistory) ? groupedHistory : [];
    const term = searchTerm.trim().toLowerCase();
    return rows.filter((item) => {
      if (activeFilter !== "all" && getItemCategory(item) !== activeFilter) return false;
      if (!term) return true;
      return `${item.ProdDesc || ""} ${item.DocNo || ""}`.toLowerCase().includes(term);
    });
  }, [groupedHistory, activeFilter, searchTerm]);

  const totalPages = Math.max(1, Math.ceil(filteredItems.length / PAGE_SIZE));

  const pagedItems = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredItems.slice(start, start + PAGE_SIZE);
  }, [filteredItems, currentPage]);

  // Reset to the first page whenever the filter or search changes.
  useEffect(() => {
    setCurrentPage(1);
  }, [activeFilter, searchTerm]);

  // Keep the current page in range if the filtered list shrinks.
  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  const goToPage = (p) => {
    setCurrentPage(Math.min(totalPages, Math.max(1, p)));
    if (listTopRef.current) {
      listTopRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  if (loading) {
    return <div style={styles.container}>{t('purchaseHistory.loading')}</div>;
  }

  // Ensure history is always an array before calling slice()
  const historyArray = Array.isArray(history) ? history : [];

  return (
    <div className="terms-modal-content" style={{ ...styles.container, color: "#F3CC5C" }}>
      <div style={styles.wrapper}>
        <button
          onClick={() => navigate("/account")}
          style={{ background: "none", border: "none", color: "#DFBD69", cursor: "pointer", fontSize: "1rem", display: "flex", alignItems: "center", gap: 6, marginBottom: 16, padding: 0 }}
        >
          &#8592; {t('purchaseHistory.back') || "Back"}
        </button>
        <h2 style={styles.heading}>{t('purchaseHistory.title')}</h2>
        <div ref={listTopRef} />
        {!(Array.isArray(groupedHistory) && groupedHistory.length > 0) ? (
          <p style={{ color: "#F3CC5C" }}>{t('purchaseHistory.noPurchases')}</p>
        ) : (
          <>
            <div style={styles.toolbar}>
              {FILTERS.map((f) => (
                <button
                  key={f.key}
                  type="button"
                  onClick={() => setActiveFilter(f.key)}
                  style={{
                    ...styles.tab,
                    ...(activeFilter === f.key ? styles.tabActive : {}),
                  }}
                >
                  {t(`purchaseHistory.${f.labelKey}`)}
                </button>
              ))}
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={t('purchaseHistory.searchPlaceholder')}
                style={styles.search}
              />
            </div>

            <p style={styles.resultsCount}>
              {t('purchaseHistory.resultsCount', { count: filteredItems.length })}
            </p>

            {filteredItems.length === 0 ? (
              <p style={{ color: "#F3CC5C" }}>{t('purchaseHistory.noResults')}</p>
            ) : (
              <>
                {pagedItems.map((item, i) => (
            <div key={item.Uid || `${item.DocNo}-${i}`} style={{ marginBottom: "32px" }}>
              <div style={styles.card}>
                {(() => {
                  const fallback = getHistoryFallbackImage(item);
                  const primary = encodeImageUrl(item.ProdThumbnail || item.ProdGif);
                  return (
                    <img
                      src={primary || fallback}
                      alt={item.ProdDesc || item.DocNo || t('purchaseHistory.title')}
                      style={styles.image}
                      loading="lazy"
                      onError={(e) => {
                        if (e.currentTarget.dataset.fallbackApplied) return;
                        e.currentTarget.dataset.fallbackApplied = "1";
                        e.currentTarget.src = fallback;
                      }}
                    />
                  );
                })()}
                <div style={styles.details}>
                  <p>
                    <strong>{item.ProdDesc}</strong>
                  </p>
                  <p>
                    {t('purchaseHistory.qty')}: {item.Qty}
                    {item.ProdDesc && item.ProdDesc.toLowerCase().includes("gem") ? ` ${t('purchaseHistory.gems')}` : ""}
                  </p>
                  <p>
                    {t('purchaseHistory.amount')}: {(item.PayModeDesc === "Card" )
                      ? `US$ ${item.Amount}`
                      : item.Amount}
                  </p>
                  {item.TotalAmount !== undefined && item.TotalAmount !== null && item.TotalAmount !== "" && (
                    <p>
                      {t('purchaseHistory.totalAmount')}: {(item.PayModeDesc === "Card" && !(item.ProdDesc && item.ProdDesc.toLowerCase().includes("gem")))
                        ? `US$ ${item.TotalAmount}`
                        : `${item.TotalAmount} ${t('purchaseHistory.gems')}`}
                    </p>
                  )}
                  {item.GroupType !== 0 && <p>{t('purchaseHistory.payMode')}: {item.PayModeDesc}</p>}
                  <p>{t('purchaseHistory.purchasedOn')}: {item.RecDate}</p>
                </div>
              </div>
            </div>
                ))}

                {totalPages > 1 && (
                  <div style={styles.pagination}>
                    <button
                      type="button"
                      onClick={() => goToPage(currentPage - 1)}
                      disabled={currentPage === 1}
                      style={{
                        ...styles.pageBtn,
                        ...(currentPage === 1 ? styles.pageBtnDisabled : {}),
                      }}
                    >
                      {`‹ ${t('purchaseHistory.prev')}`}
                    </button>
                    {getPageNumbers(totalPages, currentPage).map((p, idx) =>
                      p === "…" ? (
                        <span key={`ellipsis-${idx}`} style={{ color: "#DFBD69" }}>
                          …
                        </span>
                      ) : (
                        <button
                          key={p}
                          type="button"
                          onClick={() => goToPage(p)}
                          aria-current={p === currentPage ? "page" : undefined}
                          style={{
                            ...styles.pageBtn,
                            ...(p === currentPage ? styles.pageBtnActive : {}),
                          }}
                        >
                          {p}
                        </button>
                      )
                    )}
                    <button
                      type="button"
                      onClick={() => goToPage(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      style={{
                        ...styles.pageBtn,
                        ...(currentPage === totalPages ? styles.pageBtnDisabled : {}),
                      }}
                    >
                      {`${t('purchaseHistory.next')} ›`}
                    </button>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );

  // return (
  //   <div className="terms-modal-content" style={{ ...styles.container, color: "#F3CC5C" }}>
  //     <div style={styles.wrapper}>
  //       <h2 style={styles.heading}>Purchase History</h2>
  //       {groupedHistory.length === 0 ? (
  //         <p style={{ color: "#F3CC5C" }}>No purchases yet.</p>
  //       ) : (
  //         groupedHistory.map((group, idx) =>
  //           group.Data.map((item, i) => (
  //             <div key={item.Uid || i} style={{ marginBottom: "32px" }}>
  //               <div style={styles.card}>
  //                 {item.ProdThumbnail || item.ProdGif ? (
  //                   <img
  //                     src={item.ProdThumbnail || item.ProdGif}
  //                     alt={item.DocNo}
  //                     style={styles.image}
  //                   />
  //                 ) : (
  //                   <div style={styles.noimage}></div>
  //                 )}
  //                 <div style={styles.details}>
  //                   <p>
  //                     <strong>{item.ProdDesc}</strong>
  //                   </p>
  //                   <p>Item: {group.GroupName}</p>
  //                   <p>Qty: {item.Qty}</p>
  //                   <p>
  //                     Amount: {item.PayModeDesc === "Card" ? `$${item.Amount}` : item.Amount}
  //                   </p>
  //                   {item.TotalAmount !== undefined && item.TotalAmount !== null && item.TotalAmount !== "" && (
  //                     <p>
  //                       Total Amount: {item.PayModeDesc === "Card" ? `$${item.TotalAmount}` : item.TotalAmount}
  //                     </p>
  //                   )}
  //                   {group.GroupName !== "Free" && <p>Pay Mode: {item.PayModeDesc}</p>}
  //                   <p>Purchased on: {item.RecDate}</p>
  //                 </div>
  //               </div>
  //             </div>
  //           ))
  //         )
  //       )}
  //       {groupedHistory.length === 0 ? (
  //         <p style={{ color: "#F3CC5C" }}>No purchases yet.</p>
  //       ) : (
  //         getSortedItems().map((item, i) => (
  //           <div key={item.Uid || i} style={{ marginBottom: "32px" }}>
  //             <div style={styles.card}>
  //               {item.ProdThumbnail || item.ProdGif ? (
  //                 <img
  //                   src={item.ProdThumbnail || item.ProdGif}
  //                   alt={item.DocNo}
  //                   style={styles.image}
  //                 />
  //               ) : (
  //                 // <></>
  //                 <div style={styles.noimage}></div>
  //               )}
  //               <div style={styles.details}>
  //                 <p>
  //                   <strong>{item.ProdDesc}</strong>
  //                 </p>
  //                 <p>Item: {item.groupName}</p>
  //                 <p>Qty: {item.Qty}</p>
  //                 <p>
  //                   Amount: {item.PayModeDesc === "Card" ? `$${item.Amount}` : item.Amount}
  //                 </p>
  //                 {item.TotalAmount !== undefined && item.TotalAmount !== null && item.TotalAmount !== "" && (
  //                   <p>
  //                     Total Amount: {item.PayModeDesc === "Card" ? `$${item.TotalAmount}` : item.TotalAmount}
  //                   </p>
  //                 )}
  //                 {item.groupName !== "Free" && <p>Pay Mode: {item.PayModeDesc}</p>}
  //                 <p>Purchased on: {item.RecDate}</p>
  //               </div>
  //             </div>
  //           </div>
  //         ))
  //       )}
  //     </div>
  //   </div>
  // );
  // return (
  //   <div style={{ ...styles.container, color: "#F3CC5C" }}>
  //     <div style={styles.wrapper}>
  //       <h2 style={styles.heading}>Purchase History</h2>
  //       {groupedHistory.length === 0 ? (
  //         <p style={{ color: "#F3CC5C" }}>No purchases yet.</p>
  //       ) : (
  //         groupedHistory.map((group, idx) =>
  //           group.Data.length === 0 ? (
  //             <div key={idx} style={{ marginBottom: "32px" }}>
  //               <p>No items in this group.</p>
  //             </div>
  //           ) : (
  //             group.Data.map((item, i) => (
  //               <div key={item.Uid || i} style={{ marginBottom: "32px" }}>
  //                 <div style={styles.card}>
  //                   {item.ProdThumbnail || item.ProdGif ? (
  //                     <img
  //                       src={item.ProdThumbnail || item.ProdGif}
  //                       alt={item.DocNo}
  //                       style={styles.image}
  //                     />
  //                   ) : (
  //                     <div style={styles.image}>No Image</div>
  //                   )}
  //                   <div style={styles.details}>
  //                     <p>
  //                       <strong>{item.ProdDesc}</strong>
  //                     </p>
  //                     {/* <p>
  //                       <strong>{item.DocNo}</strong>
  //                     </p> */}
  //                     <p>Amulet Type: {group.GroupName}</p>
  //                     {/* <p>Product ID: {item.Pid}</p> */}
  //                     <p>Qty: {item.Qty}</p>
  //                     <p>Amount: {item.Amount}</p>
  //                     {/* Only show Pay Mode if not Free */}
  //                     {group.GroupName !== "Free" && <p>Pay Mode: {item.PayModeDesc}</p>}
  //                     <p>Purchased on: {item.RecDate}</p>
  //                   </div>
  //                 </div>
  //               </div>
  //             ))
  //           )
  //         )
  //       )}
  //     </div>
  //   </div>
  // );
};

export default PurchaseHistory;